'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import Image from 'next/image';
import Autoplay from 'embla-carousel-autoplay';
import { useTranslations } from 'next-intl';
import { Carousel, CarouselContent, CarouselItem } from '@/components/ui/carousel';
import { getLogoGalleries, getWebsiteTextContent } from '@/src/lib/sanity.queries';
import { urlFor } from '@/src/lib/sanity.client';

export default function ClientsSection({ locale }: { locale: string }) {
  const t = useTranslations('Home.ClientsSection');
  const isRTL = locale === 'ar';
  const dir = isRTL ? 'rtl' : 'ltr';

  type Logo = { src: string; alt: string };
  const [logos, setLogos] = useState<Logo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  type Content = {
    clientsSectionSmallTitle: string;
    clientsSectionTitle: string;
  };
  const [content, setContent] = useState<Content | null>(null);

  useEffect(() => {
    async function fetchData() {
      try {
        setIsLoading(true);
        setError(null);

        console.log('Fetching data for ClientsSection...');

        const [galleries, contentData] = await Promise.all([
          getLogoGalleries(locale),
          getWebsiteTextContent(locale),
        ]);

        console.log('Data fetched:', { galleries, contentData });

        const gallery = galleries?.[0];
        const fetched: Logo[] =
          gallery?.logos?.map((l: any) => ({
            src: urlFor(l.image).width(300).url(),
            alt: l.alt ?? '',
          })) ?? [];

        console.log('Logos processed:', fetched);
        setLogos(fetched);

        setContent({
          clientsSectionSmallTitle: contentData?.clientsSectionSmallTitle ?? '',
          clientsSectionTitle: contentData?.clientsSectionTitle ?? '',
        });
      } catch (err) {
        console.error('Failed to load logos or text content:', err);
        setError('Failed to load client logos');
      } finally {
        setIsLoading(false);
      }
    }
    fetchData();
  }, [locale]);

  const logoColumns: Logo[][] = useMemo(() => {
    if (logos.length === 0) return [];
    const extended = [...logos, ...logos, ...logos];
    const cols: Logo[][] = [];
    for (let i = 0; i < extended.length; i += 3) {
      cols.push(extended.slice(i, i + 3));
    }
    return cols;
  }, [logos]);

  const autoplayRef = useRef(
    Autoplay({
      delay: 2000,
      stopOnInteraction: false,
    })
  );
  const handleMouseEnter = () => autoplayRef.current?.stop();
  const handleMouseLeave = () => autoplayRef.current?.play();

  // Loading state
  if (isLoading) {
    return (
      <section id="ClientsSection" dir={dir} className={`h-full w-full bg-[#FFF4FF] px-5 lg:px-10`}>
        <div className="mx-auto w-full max-w-[1440px] gap-3 py-10 lg:py-20">
          <h2 className="text-lg leading-tight font-bold text-[#60215E] md:text-4xl lg:text-6xl">
            {t('Title') || 'Our Clients'}
          </h2>
          <div className="flex items-center justify-center py-20">
            <div className="text-lg text-[#60215E]">Loading client logos...</div>
          </div>
        </div>
      </section>
    );
  }

  // Error state
  if (error || logos.length === 0) {
    return (
      <section id="ClientsSection" dir={dir} className={`h-full w-full bg-[#FFF4FF] px-5 lg:px-10`}>
        <div className="mx-auto w-full max-w-[1440px] gap-3 py-10 lg:py-20">
          <h2 className="text-lg leading-tight font-bold text-[#60215E] md:text-4xl lg:text-6xl">
            {t('Title') || content?.clientsSectionTitle || 'Our Clients'}
          </h2>
          <div className="flex items-center justify-center py-20">
            <div className="text-lg text-[#60215E]">{error || 'No client logos available'}</div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="ClientsSection" dir={dir} className={`h-full w-full bg-[#FFF4FF] px-5 lg:px-10`}>
      <div className="mx-auto w-full max-w-[1440px] gap-3 py-10 lg:py-20">
        <h2 className="text-lg leading-tight font-bold text-[#60215E] md:text-4xl lg:text-6xl">
          {t('Title') || content?.clientsSectionTitle}
        </h2>
        <Carousel
          opts={{
            direction: dir,
            slidesToScroll: 1,
            loop: true,
          }}
          plugins={[autoplayRef.current]}
          className="relative w-full pb-20">
          <div className="pointer-events-none absolute top-0 bottom-0 left-0 z-10 w-12 bg-gradient-to-r from-[#FFF4FF] to-transparent sm:w-12 md:w-12 lg:w-20" />

          <CarouselContent className="flex">
            {logoColumns.map((column, columnIndex) => (
              <CarouselItem
                key={`column-${columnIndex}`}
                className="flex-none basis-1/3 pl-2 sm:pl-4 lg:basis-1/7">
                <div className="mt-4 grid w-full grid-cols-1 grid-rows-3 items-center justify-center gap-1 sm:mt-6 sm:gap-2 md:mt-8 lg:mt-10">
                  {column.map((logo, logoIndex) => (
                    <div
                      key={`logo-${columnIndex}-${logoIndex}`}
                      className="-mx-0.5 flex items-center justify-center rounded-2xl bg-white px-2 py-4 sm:-mx-1 sm:rounded-3xl sm:px-4 sm:py-6 md:px-10 md:py-8"
                      onMouseEnter={handleMouseEnter}
                      onMouseLeave={handleMouseLeave}>
                      <Image
                        src={logo.src}
                        alt={logo.alt}
                        width={80}
                        height={40}
                        className="/* base: 40 px × 80 px */ /* sm: 48 px × 96 px */ /* md: 64 px × 128 px */ /* lg: 80 px × 160 px */ /* xl: 96 px × 192 px */ 2xl:w-[14rem]/* 2xl: 112 px × 224 px */ 3xl:h-[8rem] 3xl:w-[16rem]/* 3xl: 128 px × 256 px */ h-[3rem] w-[6rem] transition-transform duration-300 hover:scale-110 sm:h-[3rem] sm:w-[6rem] sm:hover:scale-125 md:h-[4rem] md:w-[8rem] lg:h-[5rem] lg:w-[10rem] xl:h-[6rem] xl:w-[12rem] 2xl:h-[7rem]"
                      />
                    </div>
                  ))}
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>

          <div className="pointer-events-none absolute top-0 right-0 bottom-0 z-10 w-12 bg-gradient-to-l from-[#FFF4FF] to-transparent sm:w-12 md:w-12 lg:w-20" />
        </Carousel>
      </div>
    </section>
  );
}
